import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, FormControlLabel, Grid, Paper, Stack, Switch, TextField, Typography } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Popup from "../../../components/Popup";
import { getApiBaseUrl } from "../../../config/api";
import { authSuccess } from "../../../redux/userRelated/userSlice";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { FinancePageContainer, FinancePageHeader } from "./financeUi";

const EMPTY = {
    feeBankName: "",
    feeBankBranch: "",
    feeAccountNumber: "",
    feeAccountHolderName: "",
    feeMobileMoneyNumber: "",
    feePaymentInstructions: "",
    feeSmsOnPayment: true,
    feeSmsPaymentReminder: true,
    feeInstallmentReminderDays: "7",
};

function pickFeeFields(data) {
    return {
        feeBankName: data?.feeBankName || "",
        feeBankBranch: data?.feeBankBranch || "",
        feeAccountNumber: data?.feeAccountNumber || "",
        feeAccountHolderName: data?.feeAccountHolderName || "",
        feeMobileMoneyNumber: data?.feeMobileMoneyNumber || "",
        feePaymentInstructions: data?.feePaymentInstructions || "",
        feeSmsOnPayment: data?.feeSmsOnPayment !== false,
        feeSmsPaymentReminder: data?.feeSmsPaymentReminder !== false,
        feeInstallmentReminderDays: String(data?.feeInstallmentReminderDays ?? 7),
    };
}

function saveErrorMessage(err) {
    const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not save payment details.";
    if (/feeBank|feeAccount|column|schema|42703|does not exist/i.test(String(msg))) {
        return (
            `${msg} Run migration backend/prisma/migrations/20260529180000_fee_payment_submissions/migration.sql in Supabase SQL Editor, then try again.`
        );
    }
    return msg;
}

const SchoolFeePaymentSettings = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [fields, setFields] = useState(EMPTY);
    const [loadError, setLoadError] = useState("");
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [saved, setSaved] = useState(false);

    const loadedForSchoolRef = useRef("");

    useEffect(() => {
        if (!schoolId) {
            setLoadingSettings(false);
            return;
        }
        if (loadedForSchoolRef.current === schoolId) return;

        let cancelled = false;
        setLoadingSettings(true);
        setLoadError("");

        (async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Admin/${schoolId}`);
                if (cancelled) return;
                loadedForSchoolRef.current = schoolId;
                setFields(pickFeeFields(data));
            } catch (e) {
                if (!cancelled) {
                    setLoadError(saveErrorMessage(e));
                }
            } finally {
                if (!cancelled) setLoadingSettings(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [schoolId]);

    const setField = (key) => (e) => {
        setSaved(false);
        setFields((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!schoolId || saving) return;

        const payload = {
            feeBankName: fields.feeBankName.trim(),
            feeBankBranch: fields.feeBankBranch.trim(),
            feeAccountNumber: fields.feeAccountNumber.trim(),
            feeAccountHolderName: fields.feeAccountHolderName.trim(),
            feeMobileMoneyNumber: fields.feeMobileMoneyNumber.trim(),
            feePaymentInstructions: fields.feePaymentInstructions.trim(),
            feeSmsOnPayment: Boolean(fields.feeSmsOnPayment),
            feeSmsPaymentReminder: Boolean(fields.feeSmsPaymentReminder),
            feeInstallmentReminderDays: Number(fields.feeInstallmentReminderDays) || 7,
        };

        if (!payload.feeAccountNumber && !payload.feeMobileMoneyNumber) {
            setPopupMessage("Enter at least a bank account number or a mobile money number for students.");
            setShowPopup(true);
            return;
        }

        setSaving(true);
        setSaved(false);

        try {
            const { data } = await axios.put(`${getApiBaseUrl()}/Admin/${schoolId}`, payload, {
                headers: { "Content-Type": "application/json" },
            });

            const hasEntity = Boolean(data?.id || data?._id);
            if (data?.message && !hasEntity) {
                setPopupMessage(data.message);
                setShowPopup(true);
                return;
            }

            const nextFields = pickFeeFields(data);
            setFields(nextFields);
            setSaved(true);
            dispatch(
                authSuccess({
                    ...currentUser,
                    ...data,
                    ...nextFields,
                    featurePreferences: currentUser?.featurePreferences,
                }),
            );
        } catch (e) {
            setPopupMessage(saveErrorMessage(e));
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <FinancePageContainer maxWidth="md">
            <Popup message={popupMessage} setShowPopup={setShowPopup} showPopup={showPopup} />

            <FinancePageHeader
                title="Student payment details"
                subtitle="Bank and mobile money instructions shown on every student portal when they tap Make payment. Only the director should edit these."
            />

            {saved ? (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSaved(false)}>
                    Payment details saved. Students will see them on My fees and when making a payment.
                </Alert>
            ) : null}

            {loadError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            <Paper component="form" onSubmit={handleSubmit} elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    After paying to this account, students upload proof. You or the bursar verify submissions under{" "}
                    <strong>Verify student payments</strong>.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Bank name"
                            value={fields.feeBankName}
                            onChange={setField("feeBankName")}
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Branch"
                            value={fields.feeBankBranch}
                            onChange={setField("feeBankBranch")}
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Account number"
                            value={fields.feeAccountNumber}
                            onChange={setField("feeAccountNumber")}
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Account holder name"
                            value={fields.feeAccountHolderName}
                            onChange={setField("feeAccountHolderName")}
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Mobile money number"
                            value={fields.feeMobileMoneyNumber}
                            onChange={setField("feeMobileMoneyNumber")}
                            helperText="Airtel Money / TNM Mpamba if you accept them"
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Payment instructions for students"
                            value={fields.feePaymentInstructions}
                            onChange={setField("feePaymentInstructions")}
                            helperText="e.g. Use your name + roll number as the payment reference."
                            disabled={loadingSettings || saving}
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
                    Guardian SMS alerts (Twilio)
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(fields.feeSmsOnPayment)}
                                onChange={(e) => setFields((prev) => ({ ...prev, feeSmsOnPayment: e.target.checked }))}
                            />
                        }
                        label="SMS guardians when a payment is recorded"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(fields.feeSmsPaymentReminder)}
                                onChange={(e) => setFields((prev) => ({ ...prev, feeSmsPaymentReminder: e.target.checked }))}
                            />
                        }
                        label="Allow fee reminder SMS (defaulters & installments)"
                    />
                    <TextField
                        size="small"
                        type="number"
                        label="Days before installment due date to alert"
                        value={fields.feeInstallmentReminderDays}
                        onChange={setField("feeInstallmentReminderDays")}
                        sx={{ maxWidth: 320 }}
                        disabled={loadingSettings || saving}
                    />
                </Stack>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loadingSettings || saving}
                        startIcon={<SaveOutlinedIcon />}
                        sx={{ fontWeight: 700 }}
                    >
                        {saving ? "Saving…" : "Save payment details"}
                    </Button>
                </Stack>
            </Paper>
        </FinancePageContainer>
    );
};

export default SchoolFeePaymentSettings;
